import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  Map,
  CheckCircle2,
  Circle,
  Loader2,
  ChevronDown,
  ChevronUp,
  Rocket,
  Code,
  Palette,
  Globe,
  Coins,
  TrendingUp,
  Zap,
  MessageSquare,
  Megaphone,
  Percent,
  FileText,
  Target,
  Shield,
  Users,
  Vote,
  ArrowRight,
  ExternalLink,
  Flame,
  Lock,
  Layers,
  Server,
  Cpu,
  Eye,
  AlertTriangle,
  Lightbulb,
  Heart,
  Sparkles,
  ChevronRight
} from 'lucide-react'
import { cn } from '../lib/utils'
import { useJitoAPY } from '../hooks/useJitoAPY'

/* ==========================================================================
   TYPE DEFINITIONS
   ========================================================================== */

/** Milestone status enum - defines possible states for project milestones */
type MilestoneStatus = 'completed' | 'in-progress' | 'pending'

/** Interface for individual milestone data with rich metadata */
interface Milestone {
  id: number
  title: string
  status: MilestoneStatus
  icon: React.ElementType
  description: string
  details: string[]
  completedDate?: string
  estimatedDate?: string
  color: 'cyan' | 'purple' | 'green' | 'yellow' | 'magenta'
}

/** Table of contents section definition */
interface TocSection {
  id: string
  label: string
  icon: React.ElementType
  number: string
}

/* ==========================================================================
   CUSTOM HOOKS
   ========================================================================== */

/**
 * useIntersectionObserver - Triggers animations when elements scroll into view
 * Uses the Intersection Observer API for performant scroll-based animations.
 * The observer is created lazily on first observe() call to avoid timing issues
 * where refs are set before useEffect runs.
 */
function useIntersectionObserver() {
  // Initialize with the first section visible to ensure content is seen immediately
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set(['executive-summary']))
  const observerRef = useRef<IntersectionObserver | null>(null)
  const pendingRef = useRef<Set<HTMLElement>>(new Set())

  /** Lazily create the IntersectionObserver on first use */
  const getObserver = useCallback(() => {
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisibleSections((prev) => {
                const next = new Set(prev)
                next.add(entry.target.id)
                return next
              })
              // Once visible, we can stop observing it to save resources
              observerRef.current?.unobserve(entry.target)
            }
          })
        },
        // Use 0.1 threshold to ensure at least 10% is visible before triggering
        // Increased root margin to trigger slightly before the element enters the viewport
        { threshold: 0.1, rootMargin: '0px 0px -10% 0px' }
      )
      // Observe any elements that were queued before observer was ready
      pendingRef.current.forEach((el) => observerRef.current?.observe(el))
      pendingRef.current.clear()
    }
    return observerRef.current
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      observerRef.current?.disconnect()
      observerRef.current = null
    }
  }, [])

  /** Register an element to be observed for visibility (used as a callback ref) */
  const observe = useCallback((element: HTMLElement | null) => {
    if (!element) return
    const observer = getObserver()
    observer.observe(element)
    
    // Fallback: If the observer fails or takes too long, force visibility after a delay
    // This ensures content is never permanently hidden
    setTimeout(() => {
      setVisibleSections((prev) => {
        if (!prev.has(element.id)) {
          const next = new Set(prev)
          next.add(element.id)
          return next
        }
        return prev
      })
    }, 2000) // 2 second fallback
  }, [getObserver])

  return { visibleSections, observe }
}

/**
 * useActiveSection - Tracks which section is currently in the viewport
 * Used for highlighting the active table-of-contents entry
 */
function useActiveSection(sectionIds: string[]) {
  const [activeSection, setActiveSection] = useState<string>(sectionIds[0] || '')

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Find the most visible section
        const visible = entries
            .filter((e) => e.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)
  
          if (visible.length > 0) {
            const first = visible[0]
            if (first && first.target.id) {
              setActiveSection(first.target.id)
            }
          }
      },
      { threshold: [0.1, 0.3, 0.5], rootMargin: '-80px 0px -40% 0px' }
    )

    sectionIds.forEach((id) => {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    })

    return () => observer.disconnect()
  }, [sectionIds])

  return activeSection
}

/**
 * useCountUp - Animates a number counting up from 0 to the target value
 * Creates an engaging visual effect for key statistics
 */
function useCountUp(target: number, duration: number = 2000, shouldStart: boolean = false): number {
  const [count, setCount] = useState(0)
  const startedRef = useRef(false)

  useEffect(() => {
    if (!shouldStart || startedRef.current) return
    startedRef.current = true

    const startTime = Date.now()
    const step = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      // easeOutExpo for a snappy feel
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress)
      setCount(Math.round(eased * target))
      if (progress < 1) requestAnimationFrame(step)
    }
    requestAnimationFrame(step)
  }, [target, duration, shouldStart])

  return count
}

/* ==========================================================================
   SUB-COMPONENTS
   ========================================================================== */

/**
 * AnimatedSection - Wrapper that fades/slides children in when scrolled into view
 * Provides consistent scroll-triggered entrance animations across all sections
 */
function AnimatedSection({
  id,
  children,
  className,
  observe,
  isVisible,
  delay = 0
}: {
  id: string
  children: React.ReactNode
  className?: string
  observe: (el: HTMLElement | null) => void
  isVisible: boolean
  delay?: number
}) {
  return (
    <section
      id={id}
      ref={observe}
      className={cn(
        'transition-all duration-700 ease-out',
        isVisible
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-8',
        className
      )}
      style={{ transitionDelay: `${delay}ms` }}
      aria-labelledby={`${id}-heading`}
    >
      {children}
    </section>
  )
}

/**
 * StatCard - Displays an animated statistic with icon and label
 * Used in the hero section to show key platform metrics
 */
function StatCard({
  icon: Icon,
  value,
  suffix,
  label,
  color,
  shouldAnimate
}: {
  icon: React.ElementType
  value: number
  suffix?: string
  label: string
  color: string
  shouldAnimate: boolean
}) {
  const animatedValue = useCountUp(value, 2000, shouldAnimate)

  const colorClasses: Record<string, string> = {
    cyan: 'text-cyber-cyan border-cyber-cyan/30 bg-cyber-cyan/5',
    magenta: 'text-cyber-magenta border-cyber-magenta/30 bg-cyber-magenta/5',
    green: 'text-cyber-green border-cyber-green/30 bg-cyber-green/5',
    purple: 'text-cyber-purple border-cyber-purple/30 bg-cyber-purple/5'
  }

  return (
    <div className={cn(
      'glass-card border p-6 text-center group hover:scale-105 transition-transform duration-300',
      colorClasses[color] || colorClasses.cyan
    )}>
      <Icon className={cn('w-8 h-8 mx-auto mb-3 group-hover:animate-bounce', `text-cyber-${color}`)} aria-hidden="true" />
      <div className={cn('text-3xl font-bold font-header mb-1', `text-cyber-${color}`)}>
        {animatedValue}{suffix}
      </div>
      <div className="text-text-secondary text-sm">{label}</div>
    </div>
  )
}

/**
 * FlowStep - Individual step in the "How It Works" flow diagram
 * Shows numbered steps with icons and connecting arrows
 */
function FlowStep({
  number,
  icon: Icon,
  title,
  description,
  color,
  isLast = false,
  isVisible,
  delay
}: {
  number: number
  icon: React.ElementType
  title: string
  description: string
  color: string
  isLast?: boolean
  isVisible: boolean
  delay: number
}) {
  return (
    <div
      className={cn(
        'flex items-start gap-4 transition-all duration-700 ease-out',
        isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
      )}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {/* Step Number + Connector Line */}
      <div className="flex flex-col items-center flex-shrink-0">
        <div className={cn(
          'w-12 h-12 rounded-full flex items-center justify-center border-2 font-bold font-header text-lg',
          `border-cyber-${color} text-cyber-${color} bg-cyber-${color}/10`
        )}>
          {number}
        </div>
        {!isLast && (
          <div className={cn('w-0.5 h-16 mt-2', `bg-cyber-${color}/30`)} aria-hidden="true" />
        )}
      </div>

      {/* Step Content */}
      <div className="pb-8">
        <div className="flex items-center gap-2 mb-1">
          <Icon className={cn('w-5 h-5', `text-cyber-${color}`)} aria-hidden="true" />
          <h4 className={cn('text-lg font-bold', `text-cyber-${color}`)}>{title}</h4>
        </div>
        <p className="text-text-secondary text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  )
}

/**
 * FeatureCard - Displays a core feature with icon and description
 * Used in the Solution section to highlight key platform capabilities
 */
function FeatureCard({
  icon: Icon,
  title,
  description,
  color,
  isVisible,
  delay
}: {
  icon: React.ElementType
  title: string
  description: string
  color: string
  isVisible: boolean
  delay: number
}) {
  return (
    <div
      className={cn(
        'glass-card border p-6 transition-all duration-700 ease-out group',
        isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-6 scale-95',
        `border-cyber-${color}/20 hover:border-cyber-${color}/50`
      )}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className={cn(
        'w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110',
        `bg-cyber-${color}/10`
      )}>
        <Icon className={cn('w-6 h-6', `text-cyber-${color}`)} aria-hidden="true" />
      </div>
      <h4 className={cn('text-lg font-bold mb-2', `text-cyber-${color}`)}>{title}</h4>
      <p className="text-text-secondary text-sm leading-relaxed">{description}</p>
    </div>
  )
}

/* ==========================================================================
   MAIN COMPONENT
   ========================================================================== */

/**
 * Roadmap (WhitePaper & Roadmap) Page Component
 * 
 * A comprehensive combined page featuring:
 * - Full WhitePaper content (Executive Summary, Motivation, Solution, Architecture, Tokenomics, Team)
 * - Interactive project roadmap timeline
 * - Scroll-triggered animations via IntersectionObserver
 * - Sticky table-of-contents sidebar navigation
 * - Animated statistics counters
 * - Interactive flow diagrams
 * - WCAG 2.1 AA accessibility compliance
 * - Cyberpunk neon design consistent with the rest of the platform
 */
export function Roadmap(): JSX.Element {
  const { formattedApy } = useJitoAPY()

  // ── Milestone expansion state ──
  const [expandedMilestones, setExpandedMilestones] = useState<Set<number>>(new Set([4]))

  // ── Table of contents sections ──
  const tocSections: TocSection[] = [
    { id: 'executive-summary', label: 'Executive Summary', icon: FileText, number: '1' },
    { id: 'motivation', label: 'The Motivation', icon: Target, number: '2' },
    { id: 'solution', label: 'The Solution', icon: Lightbulb, number: '3' },
    { id: 'architecture', label: 'Architecture', icon: Cpu, number: '4' },
    { id: 'roadmap', label: 'Roadmap', icon: Map, number: '5' },
    { id: 'tokenomics', label: 'Tokenomics', icon: Coins, number: '6' },
    { id: 'team', label: 'Team & Values', icon: Heart, number: '7' },
    { id: 'conclusion', label: 'Conclusion', icon: Sparkles, number: '8' }
  ]

  const sectionIds = tocSections.map((s) => s.id)
  const activeSection = useActiveSection(sectionIds)
  const { visibleSections, observe } = useIntersectionObserver()

  // ── Scroll to section handler ──
  const scrollToSection = (id: string) => {
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  // ── Milestone data ──
  const milestones: Milestone[] = [
    {
      id: 1,
      title: 'Local Testing',
      status: 'completed',
      icon: Code,
      description: 'Smart contract development and local testing environment setup',
      details: [
        'Anchor program development and architecture',
        'Unit tests for all contract functions — All 42 tests passing!',
        'Local validator testing and debugging',
        'Security audit of core functionality'
      ],
      completedDate: 'November 2025',
      color: 'cyan'
    },
    {
      id: 2,
      title: 'Devnet Deployment',
      status: 'completed',
      icon: Rocket,
      description: 'Smart contracts deployed to Solana Devnet for testing',
      details: [
        'Contract deployment to Devnet',
        'Integration testing with wallets',
        'Ensuring all tests pass on Devnet',
        'Documentation and preparation for frontend integration'
      ],
      completedDate: 'December 2025',
      color: 'green'
    },
    {
      id: 3,
      title: 'V2 Liquid Staking',
      status: 'completed',
      icon: Percent,
      description: `Jito liquid staking integration for automatic yield generation (${formattedApy} APY)`,
      details: [
        'Integrated Jito Stake Pool for automatic SOL → jitoSOL conversion',
        `Pool funds now earn ${formattedApy} APY while waiting for proposals`,
        'Implemented LST-based accounting (total_pool_tokens, reserved_pool_tokens)',
        'Added recovery proposals for governance-controlled fund recovery',
        'Deployed V2 program to Devnet',
        'All 18 V2 tests passing'
      ],
      completedDate: 'January 2026',
      color: 'green'
    },
    {
      id: 4,
      title: 'UI Development',
      status: 'completed',
      icon: Palette,
      description: 'Building the cyberpunk-themed user interface with React and V3.1 integration',
      details: [
        'Frontend–Backend integration with V3.1 IDL',
        'Wallet integration and connection flows',
        'Pool creation and management interfaces',
        'Governance and voting UI components',
        'Display jitoSOL balance and yield information',
        'Responsive design for all devices',
        'Accessibility compliance checks',
        'Extensive polishing to enhance user experience and ensure transparency'
      ],
      completedDate: 'February 2026',
      color: 'purple'
    },
    {
      id: 5,
      title: 'Deploy Website',
      status: 'in-progress',
      icon: Globe,
      description: 'Production deployment of the web application',
      details: [
        'Purchase and setup of domain ctopools.com',
        'Deployment to reliable hosting provider',
        'SSL/TLS certificate setup',
        'Performance optimization and caching',
        'Monitoring and analytics integration',
        'Setup CTO Pools social media channels to start building community presence',
        'Initialize Open Beta testing for early user feedback'
      ],
      estimatedDate: 'Q1 2026',
      color: 'cyan'
    },
    {
      id: 6,
      title: 'Launch CTOP Token',
      status: 'pending',
      icon: Coins,
      description: 'Create and launch the platform governance token',
      details: [
        'Fair launch via Pump.fun',
        'Live stream for marketing',
        'Dev fees used to fund DEX as well as deployment of the smart contract to Solana Mainnet',
        'Community contest for Project Mascot design',
        'Community involvement in testing the CTO Pools platform on Devnet',
        'Initial Marketing and community Raids to spread awareness'
      ],
      estimatedDate: 'Q1 2026',
      color: 'yellow'
    },
    {
      id: 7,
      title: 'Marketing Push',
      status: 'pending',
      icon: TrendingUp,
      description: 'Get CTO Pools noticed by the Crypto Community!',
      details: [
        'Social media presence (Twitter, Discord, Telegram)',
        'Community directed listings (CoinMarketCap)',
        'Community driven Raids!',
        'Community Meme contest featuring the new mascot',
        'Outreach to active Community Takeover projects on Solana'
      ],
      estimatedDate: 'Q1 2026',
      color: 'green'
    },
    {
      id: 8,
      title: 'Mainnet Launch',
      status: 'pending',
      icon: Zap,
      description: 'Deploy V3.1 smart contracts with liquid staking to Solana Mainnet',
      details: [
        'Final security audit and code review',
        'Once CTOP token is bonded, Smart contract will be deployed to Mainnet with automatic Buy and Burn mechanism activated',
        'Real SOL transactions and Pool creation enabled',
        `V2 liquid staking active on mainnet (pools earn ${formattedApy} APY)`
      ],
      estimatedDate: 'Q2 2026',
      color: 'magenta'
    },
    {
      id: 9,
      title: 'Marketing Campaign',
      status: 'pending',
      icon: Megaphone,
      description: 'Launch comprehensive marketing and awareness campaign',
      details: [
        'Social media presence (Twitter, Discord, Telegram)',
        'Influencer partnerships and collaborations',
        'Community building and engagement',
        'Democratically guided Marketing and Community Events via CTOP governance'
      ],
      estimatedDate: 'Q2 2026',
      color: 'purple'
    },
    {
      id: 10,
      title: 'Project Announcements',
      status: 'pending',
      icon: MessageSquare,
      description: 'Ongoing updates and future feature announcements',
      details: [
        'Regular development updates',
        'Telegram bot project for real-time monitoring of pool activity',
        'New feature releases from Invent LLC',
        'Community governance decisions',
        'Roadmap updates for future phases'
      ],
      estimatedDate: 'Ongoing',
      color: 'yellow'
    }
  ]

  /** Toggle milestone expansion state */
  const toggleMilestone = (id: number) => {
    setExpandedMilestones((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  /** Get Tailwind color classes for milestone theming */
  const getColorClasses = (color: string, type: 'bg' | 'text' | 'border') => {
    const map: Record<string, Record<string, string>> = {
      cyan: { bg: 'bg-cyber-cyan/20', text: 'text-cyber-cyan', border: 'border-cyber-cyan/30' },
      purple: { bg: 'bg-cyber-purple/20', text: 'text-cyber-purple', border: 'border-cyber-purple/30' },
      green: { bg: 'bg-cyber-green/20', text: 'text-cyber-green', border: 'border-cyber-green/30' },
      yellow: { bg: 'bg-cyber-yellow/20', text: 'text-cyber-yellow', border: 'border-cyber-yellow/30' },
      magenta: { bg: 'bg-cyber-magenta/20', text: 'text-cyber-magenta', border: 'border-cyber-magenta/30' }
    }
    const fallback = map['cyan'] as Record<string, string>
    return map[color]?.[type] || fallback[type]
  }

  /** Get status icon for milestone state */
  const getStatusIcon = (status: MilestoneStatus) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-6 h-6 text-cyber-green" aria-label="Completed" />
      case 'in-progress':
        return <Loader2 className="w-6 h-6 text-cyber-purple animate-spin" aria-label="In Progress" />
      case 'pending':
        return <Circle className="w-6 h-6 text-text-muted" aria-label="Pending" />
    }
  }

  /** Get human-readable status label */
  const getStatusLabel = (status: MilestoneStatus) => {
    switch (status) {
      case 'completed': return 'Completed'
      case 'in-progress': return 'In Progress'
      case 'pending': return 'Pending'
    }
  }

  /** Calculate overall completion percentage */
  const completionPercentage = Math.round(
    (milestones.filter((m) => m.status === 'completed').length / milestones.length) * 100
  )

  // Track if hero stats section is visible to trigger count-up
  const statsVisible = visibleSections.has('executive-summary')

  return (
    <div className="min-h-screen">
      {/* ================================================================
          STICKY TABLE OF CONTENTS SIDEBAR (Desktop Only)
          ================================================================ */}
      <nav
        className="hidden xl:block fixed left-6 top-1/2 -translate-y-1/2 z-40 w-56"
        aria-label="WhitePaper table of contents"
      >
        <div className="glass-card border border-dark-border/50 p-4 rounded-xl">
          <div className="text-xs font-bold text-text-muted uppercase tracking-widest mb-4">
            Contents
          </div>
          <ul className="space-y-1" role="list">
            {tocSections.map((section) => {
              const isActive = activeSection === section.id
              const SectionIcon = section.icon
              return (
                <li key={section.id}>
                  <button
                    onClick={() => scrollToSection(section.id)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left text-sm transition-all duration-300',
                      isActive
                        ? 'bg-cyber-cyan/10 text-cyber-cyan border-l-2 border-cyber-cyan'
                        : 'text-text-muted hover:text-text-secondary hover:bg-dark-surface/50'
                    )}
                    aria-current={isActive ? 'location' : undefined}
                  >
                    <SectionIcon className={cn('w-4 h-4 flex-shrink-0', isActive ? 'text-cyber-cyan' : 'text-text-muted')} aria-hidden="true" />
                    <span className="truncate">{section.label}</span>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      </nav>

      {/* ================================================================
          MAIN CONTENT
          ================================================================ */}
      <div className="max-w-4xl mx-auto px-4 py-12 xl:ml-72 xl:mr-8">

        {/* ── HERO / TITLE ── */}
        <header className="text-center mb-16 animate-fade-in">
          {/* Version badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyber-cyan/10 border border-cyber-cyan/30 text-cyber-cyan text-sm font-semibold mb-6">
            <FileText className="w-4 h-4" aria-hidden="true" />
            <span>WhitePaper v2.0 — February 2026</span>
          </div>

          <h1
            className="text-4xl md:text-6xl font-bold font-header mb-4"
            style={{
              background: 'linear-gradient(135deg, #00E0FF 0%, #9945FF 50%, #FF00CC 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            CTO Pools
          </h1>
          <p className="text-xl md:text-2xl text-text-secondary font-light mb-2 font-display">
            The Future of Community Takeovers
          </p>
          <p className="text-text-muted text-sm max-w-xl mx-auto">
            Active Development — V2 Liquid Staking Complete · V3 Abort Mechanism Complete
          </p>

          {/* Animated divider */}
          <div className="relative mt-8 mx-auto w-48 h-0.5 overflow-hidden rounded-full" aria-hidden="true">
            <div className="absolute inset-0 bg-gradient-to-r from-cyber-cyan via-cyber-purple to-cyber-magenta animate-shimmer" />
          </div>
        </header>

        {/* ================================================================
            SECTION 1: EXECUTIVE SUMMARY
            ================================================================ */}
        <AnimatedSection
          id="executive-summary"
          observe={observe}
          isVisible={visibleSections.has('executive-summary')}
          className="mb-20"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-cyber-cyan/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-cyber-cyan" aria-hidden="true" />
            </div>
            <h2 id="executive-summary-heading" className="text-2xl md:text-3xl font-bold text-cyber-cyan !mb-0">
              1. Executive Summary
            </h2>
          </div>

          <div className="glass-card border border-cyber-cyan/20 p-8 mb-8">
            <p className="text-text-secondary leading-relaxed mb-6 text-lg">
              The cryptocurrency landscape is littered with "abandoned" projects—tokens where the original
              developers have left, but a passionate community remains. These{' '}
              <span className="text-cyber-cyan font-semibold">"Community Takeovers" (CTOs)</span>{' '}
              represent a unique opportunity but often fail due to a lack of trust, coordination, and secure infrastructure.
            </p>
            <p className="text-text-secondary leading-relaxed mb-6">
              <strong className="text-text-primary">CTO Pools</strong> is the first dedicated platform
              designed to solve this problem on the Solana blockchain. We provide a trustless, decentralized
              infrastructure that allows communities to pool funds, vote on proposals, and execute takeovers
              transparently.
            </p>

            {/* V2 Innovation highlight */}
            <div className="glass-card border border-cyber-green/30 p-6 mb-6 bg-cyber-green/5">
              <div className="flex items-start gap-3">
                <Zap className="w-6 h-6 text-cyber-green flex-shrink-0 mt-0.5" aria-hidden="true" />
                <div>
                  <h3 className="text-cyber-green font-bold text-lg mb-2 !text-lg">Key Innovation: Earn While Waiting</h3>
                  <p className="text-text-secondary text-sm leading-relaxed">
                    With our V2 upgrade, CTO Pools now integrates <strong className="text-text-primary">Jito Liquid Staking</strong>.
                    Community funds are not sitting idle — they are automatically staked to earn{' '}
                    <span className="text-cyber-green font-semibold">{formattedApy} APY (current) </span>{' '}
                    while waiting for governance decisions. This ensures that community treasuries grow over time,
                    maximizing resources available for revival efforts.
                  </p>
                </div>
              </div>
            </div>

            {/* V3 Abort highlight */}
            <div className="glass-card border border-cyber-magenta/30 p-6 bg-cyber-magenta/5">
              <div className="flex items-start gap-3">
                <Shield className="w-6 h-6 text-cyber-magenta flex-shrink-0 mt-0.5" aria-hidden="true" />
                <div>
                  <h3 className="text-cyber-magenta font-bold text-lg mb-2 !text-lg">V3 Upgrade: Safety Abort Mechanism</h3>
                  <p className="text-text-secondary text-sm leading-relaxed mb-3">
                    The V3 upgrade introduces an <strong className="text-text-primary">Abort Mechanism</strong>,
                    allowing the community to safely terminate a malicious or erroneous proposal before execution.
                    This feature adds a critical security tool, ensuring that contributors can recover their funds
                    in case of unforeseen issues.
                  </p>
                  <p className="text-text-secondary text-sm leading-relaxed">
                    <span className="text-cyber-yellow font-semibold">The use of the Abort Mechanism assumes something is wrong:</span>{' '}
                    either an error in the proposal, malicious intent (e.g., Sybil attack), or griefing behavior.
                    The built-in penalty system makes proposal attacks economically unviable and griefing
                    unappealing. Penalties are donated to the community treasury, so even abuse and attacks can benefit the community.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Animated Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={Percent}  value={7}    suffix="%" label="APY from Staking"   color="green"   shouldAnimate={statsVisible} />
            <StatCard icon={Shield}   value={30}   suffix="%" label="Quorum Required"    color="cyan"    shouldAnimate={statsVisible} />
            <StatCard icon={Users}    value={20}   suffix="%" label="Max Vote Weight"    color="purple"  shouldAnimate={statsVisible} />
            <StatCard icon={Flame}    value={1}    suffix="%" label="Platform Fee"       color="magenta" shouldAnimate={statsVisible} />
          </div>
        </AnimatedSection>

        {/* ================================================================
            SECTION 2: THE MOTIVATION
            ================================================================ */}
        <AnimatedSection
          id="motivation"
          observe={observe}
          isVisible={visibleSections.has('motivation')}
          className="mb-20"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-cyber-magenta/10 flex items-center justify-center">
              <Target className="w-5 h-5 text-cyber-magenta" aria-hidden="true" />
            </div>
            <h2 id="motivation-heading" className="text-2xl md:text-3xl font-bold text-cyber-magenta !mb-0">
              2. The Motivation
            </h2>
          </div>

          <div className="glass-card border border-cyber-magenta/20 p-8">
            <p className="text-text-secondary leading-relaxed mb-6">
              <strong className="text-text-primary">Decentralized community takeovers (CTOs)</strong>{' '}
              frequently emerge when an original team abandons a project but an engaged user base remains.
              In practice, however, these communities lack a secure and coordinated way to pool capital
              for long-term recovery and development.
            </p>

            <p className="text-text-secondary leading-relaxed mb-6">
              Today, CTO efforts tend to rely on a small number of volunteers or informal leaders
              contributing their own funds. Broader community members are often reluctant to contribute 
              meaningfully due to:
            </p>

            {/* Pain points with animated entrance */}
            <div className="grid gap-4 mb-6">
              {[
                {
                  icon: AlertTriangle,
                  title: 'High risk of scams or fund misappropriation',
                  description: 'Funds typically sit in a single wallet controlled by one person with no accountability.',
                  color: 'red' as const
                },
                {
                  icon: Lock,
                  title: 'No enforceable governance over pooled funds',
                  description: 'There are no smart-contract-enforced rules. Trust is the only mechanism and it is easily broken.',
                  color: 'yellow' as const
                },
                {
                  icon: Eye,
                  title: 'No trust-minimized mechanism for fund usage',
                  description: 'Contributors have no way to verify that funds are spent as agreed upon.',
                  color: 'magenta' as const
                }
              ].map((point, idx) => (
                <div
                  key={idx}
                  className={cn(
                    'flex items-start gap-4 glass-card border p-5 transition-all duration-500 ease-out',
                    visibleSections.has('motivation')
                      ? 'opacity-100 translate-x-0'
                      : 'opacity-0 translate-x-8',
                    `border-cyber-${point.color}/20`
                  )}
                  style={{ transitionDelay: `${200 + idx * 150}ms` }}
                >
                  <point.icon className={cn('w-6 h-6 flex-shrink-0 mt-0.5', `text-cyber-${point.color}`)} aria-hidden="true" />
                  <div>
                    <h4 className={cn('font-bold mb-1', `text-cyber-${point.color}`)}>{point.title}</h4>
                    <p className="text-text-secondary text-sm">{point.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-text-secondary leading-relaxed mb-4">
              As a result, capital contributions become fragmented, uncoordinated, and sparse. Many 
              participants choose not to contribute at all, or instead simply buy the token on the open
              market, prioritizing short-term price action over sustainable recovery.
            </p>
            <p className="text-text-secondary leading-relaxed">
              This dynamic frequently causes CTOs to devolve into{' '}
              <span className="text-cyber-magenta font-semibold">de facto pump-and-dump cycles</span>,
              even when the original intent was to revive the project.{' '}
              <strong className="text-cyber-cyan">CTO Pools</strong> was created to address this gap by
              providing a trust-minimized, governance-controlled system for pooling capital.
            </p>
          </div>
        </AnimatedSection>

        {/* ================================================================
            SECTION 3: THE SOLUTION
            ================================================================ */}
        <AnimatedSection
          id="solution"
          observe={observe}
          isVisible={visibleSections.has('solution')}
          className="mb-20"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-cyber-green/10 flex items-center justify-center">
              <Lightbulb className="w-5 h-5 text-cyber-green" aria-hidden="true" />
            </div>
            <h2 id="solution-heading" className="text-2xl md:text-3xl font-bold text-cyber-green !mb-0">
              3. The Solution: Trustless, Yield-Bearing Pools
            </h2>
          </div>

          {/* Core Features Grid */}
          <div className="grid md:grid-cols-2 gap-4 mb-10">
            <FeatureCard
              icon={Lock}
              title="Trustless Pooling"
              description="Users donate SOL to a pool identified by the target token's mint address. Funds are held in a Program-Derived Account (PDA), not a personal wallet — no single party can access them."
              color="cyan"
              isVisible={visibleSections.has('solution')}
              delay={100}
            />
            <FeatureCard
              icon={Vote}
              title="Share-Weighted Voting"
              description="Donors receive voting power proportional to their contribution. All spending decisions are made democratically with a 20% cap on voting power per wallet to prevent whale domination."
              color="purple"
              isVisible={visibleSections.has('solution')}
              delay={200}
            />
            <FeatureCard
              icon={TrendingUp}
              title="V2 Liquid Staking"
              description={`All pooled SOL is automatically converted to jitoSOL via the Jito Stake Pool, earning ${formattedApy} APY continuously. Your community treasury grows while waiting for proposals.`}
              color="green"
              isVisible={visibleSections.has('solution')}
              delay={300}
            />
            <FeatureCard
              icon={Eye}
              title="Transparent Governance"
              description="Every proposal, vote, and transaction is recorded on-chain. The community can audit the flow of funds in real-time with complete transparency."
              color="magenta"
              isVisible={visibleSections.has('solution')}
              delay={400}
            />
          </div>

          {/* How It Works Flow */}
          <h3 className="text-xl font-bold text-cyber-cyan mb-6 !text-xl">How It Works</h3>
          <div className="glass-card border border-dark-border p-8">
            <FlowStep
              number={1} icon={Layers} title="Create Pool"
              description="A community leader creates a pool for a specific token mint address. This is free — just pay Solana gas fees."
              color="cyan" isVisible={visibleSections.has('solution')} delay={100}
            />
            <FlowStep
              number={2} icon={Coins} title="Donate & Stake"
              description="Community members donate SOL. The smart contract automatically stakes this SOL with Jito, returning jitoSOL to the pool vault. Donors can withdraw their donation at any time as long as funds are not locked in an active proposal."
              color="green" isVisible={visibleSections.has('solution')} delay={200}
            />
            <FlowStep
              number={3} icon={TrendingUp} title="Earn Yield"
              description={`The pool balance grows automatically as staking rewards accrue (${formattedApy} APY (current)). Your community treasury works for you while waiting.`}
              color="purple" isVisible={visibleSections.has('solution')} delay={300}
            />
            <FlowStep
              number={4} icon={FileText} title="Propose"
              description="Donors with more than 1 SOL who have held for 24+ hours can make proposals. A proposal specifies what the money will be used for, how much SOL, and who receives it. A 1% fee is added to support the platform."
              color="yellow" isVisible={visibleSections.has('solution')} delay={400}
            />
            <FlowStep
              number={5} icon={Vote} title="Vote"
              description="All pool donors can vote Yes or No. Voting weight is based on their share of the pool, capped at 20% to prevent whales from dominating. A 30% quorum and majority Yes are required to pass."
              color="magenta" isVisible={visibleSections.has('solution')} delay={500}
            />
            <FlowStep
              number={6} icon={Zap} title="Execute"
              description="Once voting completes, any donor can Execute the proposal. Funds are sent to the specified wallet. Half of the 1% fee goes to CTO Pools, and the other half automatically buys and burns CTOP tokens, making it deflationary."
              color="cyan" isVisible={visibleSections.has('solution')} delay={600}
            />
            <FlowStep
              number={7} icon={ArrowRight} title="Rinse & Repeat"
              description="If a proposal fails, reserved funds are unlocked and returned to the pool. Keep proposing and voting until the token and community is thriving!"
              color="green" isLast isVisible={visibleSections.has('solution')} delay={700}
            />
          </div>
        </AnimatedSection>

        {/* ================================================================
            SECTION 4: TECHNICAL ARCHITECTURE
            ================================================================ */}
        <AnimatedSection
          id="architecture"
          observe={observe}
          isVisible={visibleSections.has('architecture')}
          className="mb-20"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-cyber-purple/10 flex items-center justify-center">
              <Cpu className="w-5 h-5 text-cyber-purple" aria-hidden="true" />
            </div>
            <h2 id="architecture-heading" className="text-2xl md:text-3xl font-bold text-cyber-purple !mb-0">
              4. Technical Architecture
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Smart Contract Card */}
            <div
              className={cn(
                'glass-card border border-cyber-cyan/20 p-6 transition-all duration-700',
                visibleSections.has('architecture') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              )}
              style={{ transitionDelay: '100ms' }}
            >
              <div className="flex items-center gap-3 mb-4">
                <Server className="w-6 h-6 text-cyber-cyan" aria-hidden="true" />
                <h3 className="text-lg font-bold text-cyber-cyan !text-lg !mb-0">Smart Contracts</h3>
              </div>
              <ul className="space-y-3 text-text-secondary text-sm" role="list">
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-cyber-cyan flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <span><strong className="text-text-primary">Language:</strong> Rust (Anchor Framework)</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-cyber-cyan flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <span><strong className="text-text-primary">PDA-Based Vaults:</strong> Funds strictly controlled by the program with no direct key access</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-cyber-cyan flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <span><strong className="text-text-primary">CPI Integration:</strong> Secure Cross-Program Invocations to the Jito Stake Pool</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-cyber-cyan flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <span><strong className="text-text-primary">Role-Based Access:</strong> Strict separation of duties between pool creators and donors</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-cyber-cyan flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <span><strong className="text-text-primary">Security:</strong> Reentrancy guards, checked arithmetic, comprehensive event emissions</span>
                </li>
              </ul>

              {/* Animated program ID badge */}
              <div className="mt-4 p-3 bg-dark-surface rounded-lg border border-dark-border">
                <div className="text-xs text-text-muted mb-1">Program ID (Devnet)</div>
                <code className="text-cyber-cyan text-xs font-mono break-all">
                  4T9SkpDeDyC8KWKrcGsVQ6wG14H46og6f9pBFNc1Csje
                </code>
              </div>
            </div>

            {/* Frontend Card */}
            <div
              className={cn(
                'glass-card border border-cyber-magenta/20 p-6 transition-all duration-700',
                visibleSections.has('architecture') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              )}
              style={{ transitionDelay: '250ms' }}
            >
              <div className="flex items-center gap-3 mb-4">
                <Palette className="w-6 h-6 text-cyber-magenta" aria-hidden="true" />
                <h3 className="text-lg font-bold text-cyber-magenta !text-lg !mb-0">Frontend Application</h3>
              </div>
              <ul className="space-y-3 text-text-secondary text-sm" role="list">
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-cyber-magenta flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <span><strong className="text-text-primary">Framework:</strong> React with TypeScript</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-cyber-magenta flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <span><strong className="text-text-primary">Wallet Integration:</strong> Phantom, Solflare, Backpack, and all standard Solana wallets</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-cyber-magenta flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <span><strong className="text-text-primary">Real-Time Data:</strong> Fetches on-chain account data directly for live balances and voting results</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-cyber-magenta flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <span><strong className="text-text-primary">Cyberpunk UI:</strong> Modern, engaging interface designed for the crypto-native community</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-cyber-magenta flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <span><strong className="text-text-primary">Accessibility:</strong> WCAG 2.1 AA compliant with keyboard navigation and screen reader support</span>
                </li>
              </ul>

              {/* Tech stack badges */}
              <div className="mt-4 flex flex-wrap gap-2">
                {['React', 'TypeScript', 'Vite', 'TailwindCSS', 'Anchor', 'Solana'].map((tech) => (
                  <span key={tech} className="px-2.5 py-1 text-xs font-semibold bg-cyber-magenta/10 text-cyber-magenta border border-cyber-magenta/20 rounded-full">
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Architecture Flow Diagram */}
          <div
            className={cn(
              'mt-8 glass-card border border-cyber-purple/20 p-6 transition-all duration-700',
              visibleSections.has('architecture') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            )}
            style={{ transitionDelay: '400ms' }}
          >
            <h3 className="text-lg font-bold text-cyber-purple mb-4 !text-lg">V2 Architecture Flow</h3>
            <div className="font-mono text-xs md:text-sm text-text-secondary overflow-x-auto">
              <pre className="leading-relaxed" aria-label="Architecture flow diagram">
{`  User Deposits SOL ──► CPI to Jito Stake Pool ──► Pool receives jitoSOL
                                                            │
                                               Earn ${formattedApy} APY ▼
                                                            │
  Execute Proposal ◄─── Vote ◄─── Create Proposal ◄────────┘
         │
         ▼
  jitoSOL → SOL (CPI Unstake) → 99%  to Recipient
                               → 0.5% Dev Fee
                               → 0.5% Buy & Burn CTOP`}
              </pre>
            </div>
          </div>
        </AnimatedSection>

        {/* ================================================================
            SECTION 5: ROADMAP
            ================================================================ */}
        <AnimatedSection
          id="roadmap"
          observe={observe}
          isVisible={visibleSections.has('roadmap')}
          className="mb-20"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-cyber-cyan/10 flex items-center justify-center">
              <Map className="w-5 h-5 text-cyber-cyan" aria-hidden="true" />
            </div>
            <h2 id="roadmap-heading" className="text-2xl md:text-3xl font-bold text-cyber-cyan !mb-0">
              5. Roadmap
            </h2>
          </div>

          <p className="text-text-secondary mb-8 max-w-2xl">
            Our journey is divided into clear phases, ensuring steady progress from local testing to
            full mainnet adoption.
          </p>

          {/* Progress Bar */}
          <div className="glass-card border border-dark-border p-6 mb-10">
            <div className="flex items-center justify-between mb-3">
              <span className="text-text-primary font-semibold">Overall Progress</span>
              <span className="text-cyber-cyan font-bold text-xl font-header">{completionPercentage}%</span>
            </div>
            <div className="relative h-4 bg-dark-surface rounded-full overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-cyber-cyan to-cyber-purple rounded-full transition-all duration-1000 ease-out"
                style={{ width: `${completionPercentage}%` }}
                role="progressbar"
                aria-valuenow={completionPercentage}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Project completion progress"
              />
              <div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"
                aria-hidden="true"
              />
            </div>
            <div className="mt-3 text-sm text-text-muted">
              {milestones.filter((m) => m.status === 'completed').length} of {milestones.length} milestones completed
            </div>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Vertical Timeline Line */}
            <div
              className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-cyber-cyan via-cyber-purple to-cyber-magenta opacity-30"
              aria-hidden="true"
            />

            {/* Milestones */}
            <div className="space-y-6">
              {milestones.map((milestone, index) => {
                const isExpanded = expandedMilestones.has(milestone.id)
                const IconComponent = milestone.icon

                return (
                  <div
                    key={milestone.id}
                    className={cn(
                      'relative transition-all duration-500 ease-out',
                      visibleSections.has('roadmap')
                        ? 'opacity-100 translate-y-0'
                        : 'opacity-0 translate-y-4'
                    )}
                    style={{ transitionDelay: `${index * 80}ms` }}
                  >
                    {/* Timeline Dot */}
                    <div
                      className={cn(
                        'absolute left-8 top-6 w-4 h-4 rounded-full border-4 border-dark-bg -translate-x-1/2 z-10',
                        milestone.status === 'completed' && 'bg-cyber-green',
                        milestone.status === 'in-progress' && 'bg-cyber-purple animate-pulse',
                        milestone.status === 'pending' && 'bg-dark-border'
                      )}
                      aria-hidden="true"
                    />

                    {/* Milestone Card */}
                    <div className="ml-20">
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleMilestone(milestone.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            toggleMilestone(milestone.id)
                          }
                        }}
                        className={cn(
                          'w-full text-left glass-card border transition-all duration-300 p-5 cursor-pointer',
                          'hover:scale-[1.01] focus:scale-[1.01] focus:outline-none focus:ring-2 focus:ring-cyber-cyan/50',
                          isExpanded && 'scale-[1.01]',
                          getColorClasses(milestone.color, 'border')
                        )}
                        aria-expanded={isExpanded}
                        aria-controls={`milestone-details-${milestone.id}`}
                      >
                        {/* Card Header */}
                        <div className="flex items-start space-x-4">
                          <div className={cn(
                            'w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0',
                            getColorClasses(milestone.color, 'bg')
                          )}>
                            <IconComponent
                              className={cn('w-5 h-5', getColorClasses(milestone.color, 'text'))}
                              aria-hidden="true"
                            />
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-3 mb-1.5">
                              <div>
                                <h3 className={cn(
                                  'text-lg font-bold mb-0.5 !text-lg',
                                  getColorClasses(milestone.color, 'text')
                                )}>
                                  {milestone.id}. {milestone.title}
                                </h3>
                                <p className="text-text-secondary text-sm">
                                  {milestone.description}
                                </p>
                              </div>

                              <div className="flex items-center space-x-2 flex-shrink-0">
                                {getStatusIcon(milestone.status)}
                                <ChevronDown
                                  className={cn(
                                    'w-5 h-5 text-text-muted transition-transform duration-300',
                                    isExpanded && 'rotate-180'
                                  )}
                                  aria-hidden="true"
                                />
                              </div>
                            </div>

                            {/* Date Badge */}
                            <div className={cn(
                              'inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold',
                              getColorClasses(milestone.color, 'bg'),
                              getColorClasses(milestone.color, 'text')
                            )}>
                              {milestone.completedDate || milestone.estimatedDate}
                            </div>
                          </div>
                        </div>

                        {/* Expanded Details */}
                        {isExpanded && (
                          <div
                            id={`milestone-details-${milestone.id}`}
                            className="mt-5 pt-5 border-t border-dark-border animate-slide-down"
                          >
                            <h4 className="text-sm font-semibold text-text-primary mb-3 flex items-center">
                              <span className="text-cyber-cyan mr-2">●</span>
                              Key Deliverables:
                            </h4>
                            <ul className="space-y-2" role="list">
                              {milestone.details.map((detail, idx) => (
                                <li key={idx} className="flex items-start space-x-3 text-text-secondary">
                                  <CheckCircle2
                                    className={cn(
                                      'w-4 h-4 flex-shrink-0 mt-0.5',
                                      milestone.status === 'completed' ? 'text-cyber-green' : 'text-text-muted'
                                    )}
                                    aria-hidden="true"
                                  />
                                  <span className="text-sm">{detail}</span>
                                </li>
                              ))}
                            </ul>

                            {/* Status Footer */}
                            <div className="mt-5 pt-4 border-t border-dark-border/50 flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                {getStatusIcon(milestone.status)}
                                <span className={cn(
                                  'text-sm font-semibold',
                                  milestone.status === 'completed' && 'text-cyber-green',
                                  milestone.status === 'in-progress' && 'text-cyber-purple',
                                  milestone.status === 'pending' && 'text-text-muted'
                                )}>
                                  {getStatusLabel(milestone.status)}
                                </span>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  toggleMilestone(milestone.id)
                                }}
                                className="text-sm text-cyber-cyan hover:text-cyber-cyan/80 transition-colors flex items-center space-x-1"
                                aria-label="Collapse milestone details"
                              >
                                <span>Collapse</span>
                                <ChevronUp className="w-4 h-4" aria-hidden="true" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </AnimatedSection>

        {/* ================================================================
            SECTION 6: TOKENOMICS
            ================================================================ */}
        <AnimatedSection
          id="tokenomics"
          observe={observe}
          isVisible={visibleSections.has('tokenomics')}
          className="mb-20"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-cyber-yellow/10 flex items-center justify-center">
              <Coins className="w-5 h-5 text-cyber-yellow" aria-hidden="true" />
            </div>
            <h2 id="tokenomics-heading" className="text-2xl md:text-3xl font-bold text-cyber-yellow !mb-0">
              6. Tokenomics (CTOP)
            </h2>
          </div>

          <div className="glass-card border border-cyber-yellow/20 p-8">
            <p className="text-text-secondary leading-relaxed mb-6">
              The <strong className="text-cyber-yellow">CTOP</strong> token serves as the utility token
              for the platform.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Fair Launch */}
              <div
                className={cn(
                  'glass-card border border-cyber-green/20 p-5 transition-all duration-500',
                  visibleSections.has('tokenomics') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                )}
                style={{ transitionDelay: '100ms' }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Rocket className="w-5 h-5 text-cyber-green" aria-hidden="true" />
                  <h4 className="font-bold text-cyber-green !mb-0">Fair Launch</h4>
                </div>
                <p className="text-text-secondary text-sm">
                  Launched via Pump.fun to ensure equal opportunity for all participants.
                  No presale, no insider allocation.
                </p>
              </div>

              {/* Buy & Burn */}
              <div
                className={cn(
                  'glass-card border border-cyber-magenta/20 p-5 transition-all duration-500',
                  visibleSections.has('tokenomics') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                )}
                style={{ transitionDelay: '200ms' }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Flame className="w-5 h-5 text-cyber-magenta" aria-hidden="true" />
                  <h4 className="font-bold text-cyber-magenta !mb-0">Buy &amp; Burn</h4>
                </div>
                <p className="text-text-secondary text-sm">
                  A portion of platform fees (0.5%) is used to buy back and burn CTOP,
                  creating deflationary pressure. The other 0.5% supports development.
                </p>
              </div>
            </div>

            {/* Fee Distribution Visual */}
            <div
              className={cn(
                'mt-6 glass-card border border-dark-border p-5 transition-all duration-500',
                visibleSections.has('tokenomics') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              )}
              style={{ transitionDelay: '300ms' }}
            >
              <h4 className="font-bold text-text-primary mb-3 !mb-3">CTO Pools Platform Fee Distribution (1% total on executed proposals)</h4>
              <div className="space-y-3">
                {/* 99% bar */}
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-cyber-cyan">Recipient</span>
                    <span className="text-cyber-cyan font-bold">99%</span>
                  </div>
                  <div className="h-3 bg-dark-surface rounded-full overflow-hidden">
                    <div className="h-full bg-cyber-cyan/60 rounded-full" style={{ width: '99%' }} />
                  </div>
                </div>
                {/* 0.5% dev fee bar */}
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-cyber-purple">Dev Fee</span>
                    <span className="text-cyber-purple font-bold">0.5%</span>
                  </div>
                  <div className="h-3 bg-dark-surface rounded-full overflow-hidden">
                    <div className="h-full bg-cyber-purple/60 rounded-full" style={{ width: '50%' }} />
                  </div>
                </div>
                {/* 0.5% burn bar */}
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-cyber-magenta">Buy &amp; Burn CTOP</span>
                    <span className="text-cyber-magenta font-bold">0.5%</span>
                  </div>
                  <div className="h-3 bg-dark-surface rounded-full overflow-hidden">
                    <div className="h-full bg-cyber-magenta/60 rounded-full" style={{ width: '50%' }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Developer Fees Distribution Visual */}
            <div
              className={cn(
                'mt-6 glass-card border border-dark-border p-5 transition-all duration-500',
                visibleSections.has('tokenomics') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              )}
              style={{ transitionDelay: '400ms' }}
            >
              <h4 className="font-bold text-text-primary mb-3 !mb-3">Developer Fees Distribution (Pump.fun Launch)</h4>
              <div className="space-y-3">
                {/* Marketing */}
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-cyber-cyan">Marketing</span>
                    <span className="text-cyber-cyan font-bold">25%</span>
                  </div>
                  <div className="h-3 bg-dark-surface rounded-full overflow-hidden">
                    <div className="h-full bg-cyber-cyan/60 rounded-full" style={{ width: '25%' }} />
                  </div>
                </div>
                {/* Platform */}
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-cyber-purple">Platform</span>
                    <span className="text-cyber-purple font-bold">25%</span>
                  </div>
                  <div className="h-3 bg-dark-surface rounded-full overflow-hidden">
                    <div className="h-full bg-cyber-purple/60 rounded-full" style={{ width: '25%' }} />
                  </div>
                </div>
                {/* Pay for Dex boosts */}
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-cyber-green">Pay for Dex boosts</span>
                    <span className="text-cyber-green font-bold">25%</span>
                  </div>
                  <div className="h-3 bg-dark-surface rounded-full overflow-hidden">
                    <div className="h-full bg-cyber-green/60 rounded-full" style={{ width: '25%' }} />
                  </div>
                </div>
                {/* Future development */}
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-cyber-magenta">Future development</span>
                    <span className="text-cyber-magenta font-bold">25%</span>
                  </div>
                  <div className="h-3 bg-dark-surface rounded-full overflow-hidden">
                    <div className="h-full bg-cyber-magenta/60 rounded-full" style={{ width: '25%' }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* ================================================================
            SECTION 7: TEAM & VALUES
            ================================================================ */}
        <AnimatedSection
          id="team"
          observe={observe}
          isVisible={visibleSections.has('team')}
          className="mb-20"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-cyber-purple/10 flex items-center justify-center">
              <Heart className="w-5 h-5 text-cyber-purple" aria-hidden="true" />
            </div>
            <h2 id="team-heading" className="text-2xl md:text-3xl font-bold text-cyber-purple !mb-0">
              7. Team &amp; Values
            </h2>
          </div>

          <div className="glass-card border border-cyber-purple/20 p-8 mb-6">
            {/* Team Members */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Allen */}
              <div
                className={cn(
                  'glass-card border border-cyber-cyan/20 p-6 transition-all duration-500',
                  visibleSections.has('team') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                )}
                style={{ transitionDelay: '100ms' }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyber-cyan to-cyber-purple flex items-center justify-center text-white font-bold text-lg">
                    AK
                  </div>
                  <div>
                    <h4 className="font-bold text-text-primary !mb-0">Allen Kafchinski</h4>
                    <p className="text-cyber-cyan text-sm">Founder &amp; Lead Developer</p>
                  </div>
                </div>
                <p className="text-text-secondary text-sm leading-relaxed">
                  Passionate about blockchain technology. I believe that if we provide the tools to lower
                  the barriers to entry for legitimate projects, we can reduce the dominance of scams in the
                  crypto space. CTO Pools is one tool of many more in development to help revive and
                  empower the future of crypto.
                </p>
              </div>

              {/* Invent LLC */}
              <div
                className={cn(
                  'glass-card border border-cyber-magenta/20 p-6 transition-all duration-500',
                  visibleSections.has('team') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                )}
                style={{ transitionDelay: '200ms' }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyber-magenta to-cyber-purple flex items-center justify-center text-white font-bold text-lg">
                    IN
                  </div>
                  <div>
                    <h4 className="font-bold text-text-primary !mb-0">Invent LLC</h4>
                    <p className="text-cyber-magenta text-sm">Development Studio</p>
                  </div>
                </div>
                <p className="text-text-secondary text-sm leading-relaxed">
                  Based in Las Vegas, NV, specializing in AI, blockchain, full-stack solutions and smart
                  contracts. Building innovative tools to empower the next generation of decentralized
                  communities.
                </p>
              </div>
            </div>

            {/* Core Values */}
            <h3 className="text-lg font-bold text-cyber-purple mb-4 !text-lg">Core Values</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: Eye, title: 'Transparency', description: 'All code is open source. All transactions are on-chain.', color: 'cyan' },
                { icon: Users, title: 'Community First', description: 'The community always has the final say through democratic voting.', color: 'green' },
                { icon: Lightbulb, title: 'Innovation', description: 'Pioneering yield-bearing governance models.', color: 'purple' },
                { icon: Zap, title: 'Empowerment', description: 'Giving users the tools to take control.', color: 'magenta' }
              ].map((value, idx) => (
                <div
                  key={idx}
                  className={cn(
                    'text-center p-4 glass-card border transition-all duration-500 group',
                    visibleSections.has('team') ? 'opacity-100 scale-100' : 'opacity-0 scale-90',
                    `border-cyber-${value.color}/20 hover:border-cyber-${value.color}/50`
                  )}
                  style={{ transitionDelay: `${300 + idx * 100}ms` }}
                >
                  <value.icon className={cn('w-8 h-8 mx-auto mb-2 transition-transform duration-300 group-hover:scale-110', `text-cyber-${value.color}`)} aria-hidden="true" />
                  <h4 className={cn('font-bold text-sm mb-1', `text-cyber-${value.color}`)}>{value.title}</h4>
                  <p className="text-text-muted text-xs">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </AnimatedSection>

        {/* ================================================================
            SECTION 8: CONCLUSION
            ================================================================ */}
        <AnimatedSection
          id="conclusion"
          observe={observe}
          isVisible={visibleSections.has('conclusion')}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-cyber-green/10 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-cyber-green" aria-hidden="true" />
            </div>
            <h2 id="conclusion-heading" className="text-2xl md:text-3xl font-bold text-cyber-green !mb-0">
              8. Conclusion
            </h2>
          </div>

          <div className="glass-card border border-cyber-green/20 p-8 text-center">
            <p className="text-text-secondary leading-relaxed text-lg mb-6 max-w-2xl mx-auto">
              CTO Pools is not just a tool — it's a movement. By combining the security of smart contracts
              with the financial efficiency of liquid staking, we are building the standard for how
              communities organize and succeed in the Web3 era.
            </p>

            <div
              className="text-2xl md:text-3xl font-bold font-header mb-8"
              style={{
                background: 'linear-gradient(135deg, #00E0FF 0%, #9945FF 50%, #FF00CC 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              Join us in reviving the future.
            </div>

            {/* Social/Links */}
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a
                href="https://ctopools.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-cyber-cyan/10 border border-cyber-cyan/30 rounded-lg text-cyber-cyan hover:bg-cyber-cyan/20 transition-all duration-300 hover:scale-105 text-sm font-semibold"
              >
                <Globe className="w-4 h-4" aria-hidden="true" />
                ctopools.com
                <ExternalLink className="w-3 h-3" aria-hidden="true" />
              </a>
              <a
                href="https://x.com/CtoPools"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-cyber-purple/10 border border-cyber-purple/30 rounded-lg text-cyber-purple hover:bg-cyber-purple/20 transition-all duration-300 hover:scale-105 text-sm font-semibold"
              >
                <span aria-hidden="true">𝕏</span>
                @CtoPools
                <ExternalLink className="w-3 h-3" aria-hidden="true" />
              </a>
              <a
                href="https://github.com/InventOfficial/CTO-Pools"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-cyber-green/10 border border-cyber-green/30 rounded-lg text-cyber-green hover:bg-cyber-green/20 transition-all duration-300 hover:scale-105 text-sm font-semibold"
              >
                <Code className="w-4 h-4" aria-hidden="true" />
                GitHub
                <ExternalLink className="w-3 h-3" aria-hidden="true" />
              </a>
            </div>
          </div>
        </AnimatedSection>

        {/* ── Animated bottom divider ── */}
        <div className="relative mx-auto w-full h-0.5 overflow-hidden rounded-full mb-8" aria-hidden="true">
          <div className="absolute inset-0 bg-gradient-to-r from-cyber-cyan via-cyber-purple to-cyber-magenta animate-shimmer" />
        </div>

        {/* ── Footer Note ── */}
        <div className="glass-card border border-cyber-cyan/30 p-6 text-center mb-4">
          <p className="text-text-muted text-sm">
            This whitepaper is a living document that evolves with community feedback and market conditions.
            <br />
            <span className="text-text-secondary">Last updated: February 2026 — Version 2.0</span>
          </p>
        </div>

      </div>
    </div>
  )
}

export default Roadmap
